// @flow

import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { localize } from "redux-i18n";
import { GSGrid, SectionComponent } from "@components";
import { compose } from "redux";
import moment from "moment";
import config from "@config";
import actionCreator from "@modules/shared/actionCreator";
import { getRevisionHistory } from "@modules/shared/selectors/employee-absences";
import type {
  RevisionHistoryItem,
  RevisionHistoryGridRowItem,
} from "@modules/shared/reducer/employee-absences/types";

type OwnProps = $Exact<{
  id: string,
  clientId: ?number,
  employeeId: number,
  absenceId: number,
}>;

type PropsFromDispatch = $Exact<{
  getRevisionHistory: typeof actionCreator.GetRevisionHistory,
}>;
type PropsFromState = $Exact<{
  revisionHistory: {
    content: Array<RevisionHistoryItem>,
    pagination: Object,
    totalElements: number,
  },
}>;

type Props = $Exact<{
  ...OwnProps,
  ...PropsFromDispatch,
  ...PropsFromState,
  ...PropsFromLocalize,
}>;

type ComponentState = $Exact<{
  isSectionOpen: boolean,
}>;

export function AbsenceRevisionHistory(props: Props) {
  const [isSectionOpen, setIsSelectionOpen] = useState(true);

  useEffect(() => {
    props.getRevisionHistory({
      clientId: props.clientId,
      employeeId: props.employeeId,
      absenceId: props.absenceId,
    });
  }, [props.clientId]);
  const rows = props?.revisionHistory?.content || [];

  function toggleSection() {
    setIsSelectionOpen(_ => isSectionOpen);
  }

  function compareWithPrevRecord({ row, column, rowIdx }) {
    if (!rows[rowIdx + 1]) return GSGrid.render.dateTime(row[column.key]);
    const field = column.key;
    const currentRow = row[field];
    const nextRow = rows[rowIdx + 1] && rows[rowIdx + 1][field];

    return (
      <div className={currentRow !== nextRow ? "text-warning" : ""}>
        {GSGrid.render.dateTime(currentRow)}
      </div>
    );
  }
  function compareDaysLost({ row, column, rowIdx }) {
    const field = column.key;
    if (!rows[rowIdx + 1]) return isNaN(row[field]) ? props.t("N/A") : props.t(row[field]);
    const currentRow = isNaN(row[field]) ? props.t("N/A") : row[field];
    const nextRow = rows[rowIdx + 1] ? rows[rowIdx + 1][field] : null;

    return (
      <div className={nextRow !== row[field] ? "text-warning" : ""}>{props.t(currentRow)}</div>
    );
  }
  function compareBooleanFields({ row, column, rowIdx }) {
    const field = column.key;
    const currentRow = props.t(row[field] && row[field] !== "n/a" ? "YES" : "NO");
    const nextRow = rows[rowIdx + 1];
    if (!nextRow) return currentRow;
    const nextRowValue = props.t(nextRow[field] && nextRow[field] !== "n/a" ? "YES" : "NO");

    return <div className={currentRow !== nextRowValue ? "text-warning" : ""}>{currentRow}</div>;
  }
  function compareReasons({ row, column, rowIdx }) {
    const field = column.key;
    const currentRow = row[field] !== "n/a" ? GSGrid.render.ellipsis(row[field]) : props.t("N/A");
    const nextRow = rows[rowIdx + 1];
    if (!nextRow) return currentRow;

    return <div className={row[field] !== nextRow[field] ? "text-warning" : ""}>{currentRow}</div>;
  }

  return (
    <SectionComponent
      id={props.id}
      title={<h5>{props.t("REVISIONS")}</h5>}
      wrapperClassName="flex-column"
      collapsable
      t={props.t}
      onTitleClick={toggleSection}
      isOpen={isSectionOpen}
    >
      {!!rows.length && (
        <GSGrid
          rows={rows}
          rowKeyGetter={(row: RevisionHistoryItem) => row.interactionDate}
          columns={[
            {
              key: "interactionDate",
              name: props.t("INTERACTION_DATE"),
              width: "min-content",
              renderCell: ({ row }: { row: RevisionHistoryItem }) =>
                GSGrid.render.dateTime(row.interactionDate),
            },
            {
              key: "createdBy",
              align: "left",
              name: props.t("USER"),
              width: "min-content",
            },
            {
              key: "startDate",
              name: props.t("START_DATE"),
              renderCell: compareWithPrevRecord,
            },
            {
              key: "fitDate",
              name: props.t("FIT_DATE"),
              renderCell: compareWithPrevRecord,
            },
            {
              key: "rtwDate",
              name: props.t("RTW_DATE"),
              renderCell: compareWithPrevRecord,
            },
            {
              key: "daysLost",
              name: props.t("DAYS_LOST"),
              renderCell: compareDaysLost,
            },
            {
              key: "absenceReason",
              align: "left",
              name: props.t("REASON"),
              renderCell: compareReasons,
            },
            {
              key: "isConsentGiven",
              name: props.t("CONSENT_GIVEN"),
              renderCell: compareBooleanFields,
            },
            {
              key: "isWorkRelated",
              name: props.t("WORK_RELATED"),
              renderCell: compareBooleanFields,
            },
            {
              key: "isAccidentRelated",
              name: props.t("ACCIDENT_RELATED"),
              renderCell: compareBooleanFields,
            },
            {
              key: "status",
              name: props.t("STATUS"),
              renderCell: ({ row }: { row: RevisionHistoryItem }) =>
                GSGrid.render.statusPill(props.t(row.status)),
            },
          ]}
        />
      )}
    </SectionComponent>
  );
}

const mapStateToProps = (state, ownProps): PropsFromState => {
  return {
    revisionHistory: getRevisionHistory(state),
  };
};

const mapDispatchToProps: PropsFromDispatch = {
  getRevisionHistory: actionCreator.GetRevisionHistory,
};

export default compose(
  localize(),
  connect(mapStateToProps, mapDispatchToProps),
)(AbsenceRevisionHistory);
